interface ApiError extends Error {
  status?: number
}

export function isErrorWithStatus(error: unknown): error is ApiError {
  return typeof error === "object" && error !== null && "status" in error
}

export function isUnauthorizedError(error: unknown) {
  return isErrorWithStatus(error) && error.status === 401
}

export function isBadRequestError(error: unknown) {
  return isErrorWithStatus(error) && error.status === 400
}

export function isNotFoundError(error: unknown) {
  return isErrorWithStatus(error) && error.status === 404
}

export function isInternalServerError(error: unknown) {
  return isErrorWithStatus(error) && error.status === 500
}

export function hasErrorMessage(
  error: unknown,
): error is { data: { message: string } } {
  const isObject = typeof error === "object" && error !== null
  if (!isObject) {
    return false
  }

  const hasData = "data" in error
  if (!hasData) {
    return false
  }

  const data = error.data
  const isDataObject = typeof data === "object" && data !== null
  if (!isDataObject) {
    return false
  }

  return "message" in data
}

/** NestJS errors carry the human-readable message at `data.message` — a
 *  string, or a string[] for class-validator failures. Returns the joined
 *  message(s), or `fallback` when the payload has none. */
export function getServerErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!hasErrorMessage(error)) {
    return fallback
  }
  const message: string | string[] = error.data.message
  return Array.isArray(message) ? message.join("; ") : message
}

export async function uploadFileByPreSignedUrl(
  preSignedUrl: string,
  file: File,
  options?: Omit<RequestInit, "method" | "body">,
) {
  const response = await fetch(preSignedUrl, {
    ...options,
    method: "PUT",
    headers: {
      "Content-Type": file.type,
      ...options?.headers,
    },
    body: file,
  })

  return response.ok
}
